import React, { useState, useEffect, type FC } from 'react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { SearchDropdownProps } from '../../utility/types';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SearchDropdown: FC<SearchDropdownProps> = ({ initialValue, selections, onSelect }) => {
  const [ query, setQuery ] = useState('');
  const [ selected, setSelected ] = useState(initialValue);

  useEffect(() => {
    onSelect(selected);
  }, [ selected ]);

  const options = [ ...selections ];
  options.unshift({ key: '' });
  const filteredSelections =
    query === ''
      ? options
      : options.filter((selection) => {
        const includesKey = selection.key.toLowerCase().includes(query.toLowerCase());
        const includesSubtitle = selection.subtitle?.toLowerCase().includes(query.toLowerCase()) ?? false;
        return includesKey || includesSubtitle;
      });

  return (
    <Combobox as="div" value={selected} onChange={setSelected}>
      <div className="relative mt-2">
        <Combobox.Input
          className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          onChange={(event) => setQuery(event.target.value)}
          displayValue={(selection: string) => selection}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>

        {filteredSelections.length > 0 && (
          <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredSelections.map((selection) => (
              <Combobox.Option
                key={selection.key}
                value={selection.key}
                className={({ active }) =>
                  classNames(
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    <span className={classNames('block truncate', selected && 'font-semibold')}>{selection.key == '' ? '-' : `${selection.key} - ${selection.subtitle}`.substring(0, 50)}</span>

                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          active ? 'text-white' : 'text-indigo-600'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

export default SearchDropdown;
